import React, { FC, useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { FormControlLabel, Grid, Radio, RadioGroup, RadioProps, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import { useHistory, useLocation } from 'react-router-dom'
import { useParams } from 'react-router'
import { toast } from 'react-toastify'
import { Loader } from '../../../components/Loader'
import { useRest } from '../../../components/Context'
import Company from '../../../images/onBoarding/company-1.svg'
import Individual from '../../../images/onBoarding/Individual.svg'
import { GreenButton } from '../../../components/Buttons/GreenButton'
import { TitleOnBoarding } from '../../../components/TitleOnBoarding'
import { setStepperFormData } from '../../../services/setStepperFormData'
import { selectAccountType } from '../../../store/actions'
import { AccountType } from '../../../constants/accountType'

type IProps = {
  title: string
}

const useStyles = makeStyles({
  container: {
    margin: '0 auto',
    maxWidth: 1184,
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 'bold',
  },
  label: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#3E3E3E',
  },
  form: {
    margin: '0 auto',
    maxWidth: 407,
    width: '100%',
    textAlign: 'center',
  },
  justify: {
    justifyContent: 'space-between',
    marginTop: 96,
    marginBottom: 96,
  },
  active: {
    opacity: 0.7,
    '&:hover': {
      // opacity: 1,
    },
  },
  root: {
    justifyContent: 'space-between',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&.MuiTypography-body1': {
      fontSize: 18,
      fontWeight: 'bold',
    },
  },
  compIcon: {
    width: 34,
    height: 34,
    marginRight: 8,
    backgroundImage: `url(${Company})`,
    backgroundSize: 'cover',
    'input:hover ~ &': {
      // backgroundColor: 'transparent',
    },
    'input:disabled ~ &': {
      opacity: 0.7,
    },
  },
  indIcon: {
    width: 34,
    height: 34,
    marginRight: 8,
    backgroundImage: `url(${Individual})`,
    backgroundSize: 'cover',
    'input:hover ~ &': {
      // backgroundColor: 'transparent',
    },
    'input:disabled~&': {
      opacity: 0.7,
    },
  },
})

const IndividualRadio = (props: RadioProps) => {
  const classes = useStyles()

  return (
    <Radio
      className={classes.root}
      color="default"
      checkedIcon={<span className={clsx(classes.indIcon)} />}
      icon={<span className={classes.indIcon} />}
      {...props}
    />
  )
}

const CompanyRadio = (props: RadioProps) => {
  const classes = useStyles()

  return (
    <Radio
      className={classes.root}
      color="default"
      checkedIcon={<span className={classes.compIcon} />}
      icon={<span className={classes.compIcon} />}
      {...props}
    />
  )
}

export const SelectAccountType: FC<IProps> = ({ title }) => {
  const classes = useStyles()
  // eslint-disable-next-line
  const [loading, setLoading] = useState<boolean>(true)
  // eslint-disable-next-line
  const [data, setData] = useState<any>({})
  const [activeStep] = useState(1)
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { rxios } = useRest()
  const { uniqKey } = useParams() as { [key: string]: string }
  const { type } = data

  const { handleSubmit } = useForm()

  const getData = useCallback(async () => {
    let cleanupFunction = false
    setLoading(true)

    rxios.get(`company/onboarding/${uniqKey}`).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        setData(next)
        dispatch(selectAccountType(next.type))

        if (next.termsAccepted) {
          history.push({
            ...location,
            state: {
              activeStep: 8,
            },
          })
        }

        if (next.dataEntered && !next.termsAccepted) {
          history.push({
            ...location,
            state: {
              activeStep: 7,
            },
          })
        }
        if (!cleanupFunction) setLoading(false)
      },
      (error) => {
        toast.error(error.response.data.message)
        history.push({
          ...location,
          state: {
            activeStep: 9,
          },
        })
      },
      () => {
        setLoading(false)
      },
    )
    cleanupFunction = true
  }, [dispatch, history, location, rxios, uniqKey])

  useEffect(() => {
    ;(async () => {
      await getData()
    })()
    // eslint-disable-next-line
  }, [])

  const onSubmit = () => {
    if (type === AccountType.COMPANY) {
      const newData = {
        company: {
          firstName: data.personalContact?.split(' ')[0],
          lastName: data.personalContact?.split(' ')[1],
          companyName: data.companyName,
          email: data.companyEmail,
        },
      }

      setStepperFormData(newData)

      history.push({
        ...location,
        state: {
          activeStep,
        },
      })
    } else if (type === AccountType.INDIVIDUAL) {
      const newData = {
        user: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
        },
      }
      setStepperFormData(newData)
      history.push({
        ...location,
        state: {
          activeStep: 6,
        },
      })
    }
  }

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Grid className={classes.container} container direction="column" alignItems="center">
          <TitleOnBoarding title={title} activeStep={activeStep} />
          <Grid item container direction="row" justifyContent="space-between" alignItems="center">
            <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
              <RadioGroup row className={classes.justify} value={type} name="type">
                <FormControlLabel
                  className={type !== 'individual' ? classes.active : ''}
                  value={'individual'}
                  control={<IndividualRadio />}
                  label={<Typography className={classes.label}>Individual</Typography>}
                  disabled={type !== 'individual'}
                />
                <FormControlLabel
                  className={type !== 'company' ? classes.active : ''}
                  value={'company'}
                  control={<CompanyRadio />}
                  label={<Typography className={classes.label}>Company</Typography>}
                  disabled={type !== 'company'}
                />
              </RadioGroup>
              <GreenButton type="submit" width={321} mb={247} text={'NEXT'} />
            </form>
          </Grid>
        </Grid>
      )}
    </>
  )
}
